/// <reference types="@angular/localize" />

import { enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding, withEnabledBlockingInitialNavigation } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { LOCALE_ID, ErrorHandler } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MissingTranslationHandler, TranslateModule } from '@ngx-translate/core';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard, MsalInterceptor, MsalModule, MsalService } from '@azure/msal-angular';
import { ChartsModule } from '@progress/kendo-angular-charts';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { NgxJsonViewerModule } from 'ngx-json-viewer';
import { NgxSpinnerModule } from 'ngx-spinner';
import { MbscModule } from '@mobiscroll/angular';

import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';
import { environment } from './environments/environment';
import { AppMissingTranslationHandler } from './app/shared/helpers/app-missing-translation-handler';
import { MSALGuardConfigFactory, MSALInstanceFactory, MSALInterceptorConfigFactory } from './app/core/services/auth-service/auth-config';
import { OverrideTenantInterceptor } from './app/shared/helpers/override-tenant.interceptor';
import { UploadInterceptor } from './app/shared/helpers/upload.interceptor';
import { ErrorIntercept } from './app/shared/helpers/error.interceptor';
import { ErrorHandlerService } from './app/core/services/custom-services/error-handler.service';
import { LocalStorageService } from './app/core/services/custom-services/localstorage.service';
import { UserService } from './app/core/services/http-services/common/user.service';
import { AppNotificationService } from './app/core/services/custom-services/notification.service';
import { ConfigurationService } from './app/core/services/custom-services/configuration.service';
import { AuthService } from './app/core/services/auth-service/auth.service';
import { VmService } from './app/core/services/http-services/common/vm.service';
import { EntityService } from './app/core/services/http-services/model/entity.service';
import { EntitySimpleService } from './app/core/services/http-services/model/entity-simple.service';
import { EntityPropertyService } from './app/core/services/http-services/model/entity-property.service';
import { BeamMaterialLinesService } from './app/core/services/http-services/gluelam/beam-material-lines.service';
import { ApplicationInsightsService } from './app/core/services/custom-services/application-insights.service';
import { UnitService } from './app/core/services/http-services/common/unit-service';
import { MachineService } from './app/core/services/http-services/operative/machine.service';
import { MachineGroupService } from './app/core/services/http-services/operative/machine-group.service';
import { EntityGroupService } from './app/core/services/custom-services/entity-group.service';
import { RamConfigurationService } from './app/core/services/http-services/operative/ram-configuration.service';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { Title } from '@angular/platform-browser';
import { RoleGuard } from './app/core/services/auth-service/role.guard';

import localeAT from '@angular/common/locales/de-AT';
import localeHI from '@angular/common/locales/hi';
import localeSE from '@angular/common/locales/sv';
import localeFI from '@angular/common/locales/fi';
import { DatePipe, registerLocaleData } from '@angular/common';
import { TrackHeadersInterceptor } from '@app/shared/helpers/track-headers.interceptor';

registerLocaleData(localeSE, 'sv-SE');
registerLocaleData(localeAT, 'de-AT');
registerLocaleData(localeHI, 'hi-IN');
registerLocaleData(localeFI, 'fi-FI');

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideAnimations(),
    provideRouter(appRoutes, withEnabledBlockingInitialNavigation(), withComponentInputBinding()),
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(
      FormsModule,
      ReactiveFormsModule,
      TranslateModule.forRoot({
        loader: provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json', useHttpBackend: true }),
        missingTranslationHandler: { provide: MissingTranslationHandler, useClass: AppMissingTranslationHandler },
        fallbackLang: 'en',
      }),
      MsalModule,
      ChartsModule,
      NotificationModule,
      NgxJsonViewerModule,
      DialogModule,
      NgxSpinnerModule,
      MbscModule
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OverrideTenantInterceptor,
      deps: [LocalStorageService],
      multi: true,
    },
    {
      provide: LOCALE_ID,
      deps: [UserService],
      useFactory: (settingsService: UserService) => settingsService.getLocaleStr(),
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UploadInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TrackHeadersInterceptor,
      deps: [LocalStorageService],
      multi: true,
    },
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorIntercept,
      deps: [AppNotificationService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    RoleGuard,
    ConfigurationService,
    AuthService,
    VmService,
    AppNotificationService,
    Title,
    EntityService,
    EntitySimpleService,
    EntityPropertyService,
    BeamMaterialLinesService,
    ApplicationInsightsService,
    UnitService,
    MachineService,
    MachineGroupService,
    EntityGroupService,
    RamConfigurationService,
    DatePipe,
  ],
}).catch(err => console.log(err));
